import { useServiceStore } from "../store/service";
import defaultProfile, { asyncRoute } from "./default";
import type { ConfigSection, GameProfile, type Menu, ProfileCtx } from "./types";
import { MenuItem } from "./types";
import { commonMenuItems } from "./common";
import { faClockRotateLeft, faFileImport, faSliders } from "@fortawesome/pro-solid-svg-icons";
import { i18n } from "@/i18n";
const { t } = i18n.global;

const extendedSettingsMenuItem = new MenuItem(
  "extended-settings",
  faSliders,
  asyncRoute(() => import("../components/views/config/scum/ScumExtendedConf.vue")),
);

const createMenu = (): Menu => {
  const menu = defaultProfile().menu;
  // menu[0].items = [status, settings, fileManager] — insert Extended Settings between settings and fileManager
  menu[0].items.splice(2, 0, extendedSettingsMenuItem);
  // Save Backups + Save Import (TR-5209) sit directly below File Manager, matching Palworld's
  // ordering, with Logs kept last. Labels come from the existing generic ngp/route/* keys.
  menu[0].items.push(
    new MenuItem(
      "save-backups",
      faClockRotateLeft,
      asyncRoute(() => import("../components/views/scum/SaveBackups.vue")),
    ),
    new MenuItem(
      "save-import",
      faFileImport,
      asyncRoute(() => import("../components/views/scum/SaveImport.vue")),
    ),
  );
  menu[0].items.push(commonMenuItems.logs);
  return menu;
};

// Main Settings page: core config + moderation tables + the WhalleyBot feature.
// Advanced tuning (gameplay, Economy, vehicles, features, respawn) lives on the
// separate Extended Settings page (see ScumExtendedConf.vue).
const configSections: ConfigSection[] = [
  { component: "ConfSection", props: { name: "general" } },
  { component: "TableSection", props: { name: "adminList" } },
  { component: "TableSection", props: { name: "adminSettingList" } },
  { component: "TableSection", props: { name: "permittedList" } },
  { component: "TableSection", props: { name: "exclusiveUsers" } },
  { component: "TableSection", props: { name: "mutedUsers" } },
  { component: "TableSection", props: { name: "bannedList" } },
  { component: "ConfSection", props: { name: "whalleybot" } },
  { component: "ConfSection", props: { name: "modding" } },
];

const join = () => {
  window.open(t("ngp/settings/section/scum_wiki/link"), "_blank", "noopener,noreferrer");
};

const scumProfile = (_ctx: ProfileCtx): Partial<GameProfile> => ({
  menu: createMenu(),
  configSections,
  accessDataFn() {
    const service = useServiceStore();
    const gameServer = service.ctx.sys?.gameServer;
    const serverPort = service.ctx.profile?.displayPorts?.serverPort;
    if (!gameServer || !serverPort) {
      return undefined;
    }
    const serverIp = gameServer.serverIp;
    return `${serverIp}:${serverPort}`;
  },
  join: {
    buttonTextKey: "ngp/settings/section/scum_wiki",
    joinFn: join,
  },
});

export default scumProfile;

// Studio test servers (backend ScumDev profile): same panel, with the branch and command line section on top,
// like the Conan Exiles UE5 officials profile.
export const scumDev = (ctx: ProfileCtx): Partial<GameProfile> => ({
  ...scumProfile(ctx),
  configSections: [{ component: "ConfSection", props: { name: "studio" } }, ...configSections],
});
